// src/constants/publicationTimes.js
//
// Centralized publication-time notes, per market x product (Phase 1 of the
// price-forecast expansion; spec section 6 of the 2026-08-25 brief).
//
// Rules encoded here, deliberately:
//   * Times are MARKET-LOCAL, always labelled with their IANA zone (or CET
//     where the underlying pan-EU gate itself is defined in CET). Bare times
//     with no zone are never shown.
//   * The "~" is meaningful: publication depends on the source and retry
//     chains exist. Copy is factual, no promises.
//   * One string set per market x product, in ONE module - Phases 3/4 update
//     these entries per market instead of hunting through JSX.
//
// Each entry: { forecast: string|null, actuals: string|null }. forecast=null
// means PYTHIA publishes no forecast for that market/product yet - the note
// then shows only the actuals half.

// Γιατί εισάγονται εδώ: τα DEFAULT παρακάτω γράφτηκαν όταν κάθε αγορά
// του dropdown είχε και IDA και balancing. Η BG (17/9/2026) είναι η πρώτη που δεν
// έχει: χωρίς αυτόν τον έλεγχο η καρτέλα Intraday θα έλεγε στον χρήστη
// "Gate 15:00 CET · ingested ~16:00 Europe/Sofia" για μια ροή που δεν υπάρχει.
import {
  BALANCING_SUPPORTED_MARKETS,
  IDA_SUPPORTED_MARKETS,
  normalizeMarketZone,
} from "../utils/marketSupport";

const NO_IDA_SOURCE = {
  forecast: null,
  actuals: "No intraday-auction data source for this market yet",
};
const NO_BALANCING_SOURCE = {
  forecast: null,
  actuals: "No balancing data source for this market yet",
};
const hasIda = (market) => IDA_SUPPORTED_MARKETS.has(normalizeMarketZone(market));
const hasBalancing = (market) =>
  BALANCING_SUPPORTED_MARKETS.has(normalizeMarketZone(market));

const TZ = {
  GR: "Europe/Athens",
  FR: "Europe/Paris",
  DE: "Europe/Berlin",
  NL: "Europe/Amsterdam",
  HU: "Europe/Budapest",
  BG: "Europe/Sofia",
};

export const PUBLICATION_TIMES = {
  dam: {
    GR: {
      forecast: `In-house ~08:45 ${TZ.GR} (D-1)`,
      actuals: `SDAC results ~12:45–13:00 CET (D-1) · ingested ~14:00 ${TZ.GR} via HEnEx`,
    },
    FR: {
      forecast: `In-house ~08:45 ${TZ.FR} (D-1)`,
      actuals: `SDAC results ~12:45–13:00 CET (D-1) · ingested ~14:40 ${TZ.FR} via ENTSO-E`,
    },
    DE: {
      forecast: `In-house ~08:45 ${TZ.DE} (D-1)`,
      actuals: `SDAC results ~12:45–13:00 CET (D-1) · ingested ~14:40 ${TZ.DE} via ENTSO-E`,
    },
    NL: {
      forecast: `In-house ~08:45 ${TZ.NL} (D-1)`,
      actuals: `SDAC results ~12:45–13:00 CET (D-1) · ingested ~14:40 ${TZ.NL} via ENTSO-E`,
    },
    HU: {
      forecast: `In-house ~08:45 ${TZ.HU} (D-1)`,
      actuals: `SDAC results ~12:45–13:00 CET (D-1) · ingested ~14:40 ${TZ.HU} via ENTSO-E`,
    },
    // BG: forecast=null γιατί η πλατφόρμα ΔΕΝ δημοσιεύει πρόβλεψη για τη Βουλγαρία
    // (καμία ροή Forecast Valley, καμία εγγραφή στο dam_forecast_v1/markets.py) -
    // όχι επειδή λείπει το κείμενο. Το PublicationNote τότε δείχνει μόνο το μισό
    // "actuals", που είναι και η αλήθεια. Χωρίς εγγραφή εδώ η getPublicationNote
    // επιστρέφει null (το μπλοκ dam δεν έχει DEFAULT) και η γραμμή
    // προέλευσης εξαφανίζεται εντελώς - και για τις πραγματικές τιμές που έχει.
    BG: {
      forecast: null,
      actuals: `SDAC results ~12:45–13:00 CET (D-1) · ingested ~14:40 ${TZ.BG} via ENTSO-E`,
    },
  },
  ida1: {
    GR: {
      forecast: `~14:15 ${TZ.GR} (D-1)`,
      actuals: `Gate 15:00 CET (D-1), results ~15:15–15:30 CET · ingested ~16:00 ${TZ.GR} via HEnEx`,
    },
    DEFAULT: (market) =>
      hasIda(market)
        ? {
            forecast: null,
            actuals: `Gate 15:00 CET (D-1), results ~15:15–15:30 CET · ingested ~16:00 ${TZ[market] || "market-local"}`,
          }
        : NO_IDA_SOURCE,
  },
  ida2: {
    GR: {
      forecast: `~17:00 ${TZ.GR} (D-1)`,
      actuals: `Gate 22:00 CET (D-1) · ingested ~23:00 ${TZ.GR} via HEnEx`,
    },
    DEFAULT: (market) =>
      hasIda(market)
        ? {
            forecast: null,
            actuals: `Gate 22:00 CET (D-1) · ingested ~23:00 ${TZ[market] || "market-local"}`,
          }
        : NO_IDA_SOURCE,
  },
  ida3: {
    GR: {
      forecast: `~09:00 ${TZ.GR} (delivery day)`,
      actuals: `Gate 10:00 CET (delivery day) · ingested ~11:00 ${TZ.GR} via HEnEx`,
    },
    DEFAULT: (market) =>
      hasIda(market)
        ? {
            forecast: null,
            actuals: `Gate 10:00 CET (delivery day) · ingested ~11:00 ${TZ[market] || "market-local"}`,
          }
        : NO_IDA_SOURCE,
  },
  xbid: {
    GR: {
      forecast: null,
      actuals: `HEnEx continuous-trading workbook, late on delivery day · ingested ~23:50 ${TZ.GR}`,
    },
    DEFAULT: () => ({
      forecast: null,
      actuals: "No continuous-intraday data source for this market yet",
    }),
  },
  balancing_actuals: {
    GR: {
      forecast: null,
      actuals:
        `Reserve capacity: D-1 ISP results via ENTSO-E, refreshed every 3 h · ` +
        `Imbalance: weekly ENTSO-E batches, typically 4–11 days after delivery`,
    },
    DEFAULT: (market) =>
      hasBalancing(market)
        ? {
            forecast: null,
            actuals:
              "ENTSO-E, refreshed every 3 h · imbalance is published ex-post with a source-dependent delay",
          }
        : NO_BALANCING_SOURCE,
  },
  xbid_forecast: {
    GR: {
      // Two rungs of one ladder. The early one is less accurate on purpose -
      // it runs before IDA2 exists - and is there so a bid into IDA2 can be
      // placed with a forecast in hand rather than after the fact.
      forecast:
        `Early ~17:15 ${TZ.GR} (D-1) once IDA1 has cleared · ` +
        `Late ~23:20 ${TZ.GR} (D-1), after the 22:00 CET IDA2 gate and before delivery day begins`,
      actuals:
        `Partial workbook during the delivery day · FINAL version ~09:25 ${TZ.GR} on D+1, ` +
        `which is when scores appear`,
    },
    DEFAULT: () => ({
      forecast: null,
      actuals: "No continuous-intraday data source for this market yet",
    }),
  },
  balancing_forecast: {
    GR: {
      forecast: `~15:30 ${TZ.GR} (D-1), ahead of the 16:45 ${TZ.GR} ISP1 gate`,
      actuals:
        `Reserve capacity actuals: D-1 evening via ENTSO-E · ` +
        `Imbalance actuals (and its scores): typically 4–11 days later, weekly batches`,
    },
    DEFAULT: () => ({ forecast: null, actuals: null }),
  },
};

export function getPublicationNote(productKey, market) {
  const product = PUBLICATION_TIMES[productKey];
  if (!product) return null;
  const entry = product[market] ?? (typeof product.DEFAULT === "function" ? product.DEFAULT(market) : null);
  if (!entry || (!entry.forecast && !entry.actuals)) return null;
  return entry;
}
